import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  BugOutlined,
  ThunderboltOutlined,
  StarOutlined,
  ToolOutlined,
  SafetyOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import { useAppSelector, useAppDispatch } from "../../store/hooks";
import { logout, setAccess } from "../../store/authSlice";
import { denyAccess } from "../../api/access";
import { useViewport } from "../../hooks/useViewport";
import ProfileDropdown from "../../components/ProfileDropdown";
import BackButton from "../../components/BackButton";
import { changelogData, type ChangeType } from "./changelogData";

const R = {
  bg: "var(--color-canvas)",
  surface: "var(--color-surface)",
  border: "var(--color-shade-2)",
  text: "var(--color-ink)",
  textSoft: "var(--color-body)",
  textMuted: "var(--color-muted)",
  ink: "var(--color-ink)",
  brand: "var(--color-brand-ink)",
  brandDeep: "var(--color-brand-deep-ink)",
  brandTint: "var(--color-brand-tint)",
  mono: '"JetBrains Mono", ui-monospace, monospace',
};

const FONT =
  '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, system-ui, sans-serif';

// Тоналност на типовете: икон-чип + цвят на текста за иконата.
// Всички нюанси са меки, за да се усаждат на warm beige фона.
const typeConfig: Record<
  ChangeType,
  {
    tint: string;
    ink: string;
    icon: React.ReactNode;
    labelKey: string;
  }
> = {
  feature: {
    tint: R.brandTint,
    ink: R.brandDeep,
    icon: <StarOutlined />,
    labelKey: "changelog.typeFeature",
  },
  improvement: {
    tint: "var(--color-fill)",
    ink: "var(--color-body)",
    icon: <ThunderboltOutlined />,
    labelKey: "changelog.typeImprovement",
  },
  fix: {
    tint: "var(--color-danger-tint)",
    ink: "var(--color-danger-ink)",
    icon: <BugOutlined />,
    labelKey: "changelog.typeFix",
  },
  breaking: {
    tint: "var(--color-fill)",
    ink: "var(--color-brand-deep-ink)",
    icon: <ToolOutlined />,
    labelKey: "changelog.typeBreaking",
  },
  security: {
    tint: "var(--color-ok-tint)",
    ink: "var(--color-ok-deep)",
    icon: <SafetyOutlined />,
    labelKey: "changelog.typeSecurity",
  },
};

export default function Changelog() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isNarrow } = useViewport();

  const names = useAppSelector((s) => s.auth.names);
  const username = useAppSelector((s) => s.auth.username);
  const access = useAppSelector((s) => s.auth.access);

  const displayName = names || username || "";

  const latestVersion = changelogData[0]?.version;

  const handleLogout = async () => {
    // Същият guard като във всички останали екрани (Модули, Резервации,
    // Отстъпки, Доставки). Без него при потвърден ограничен достъп „Изход"
    // праща logout със СЛУЖИТЕЛСКИЯ токен: BE връща споделения токен и редът
    // в user_sessions остава активен (лицензът виси зает), а FE трие токена
    // локално — тоест никой не може да го освободи.
    if (access) {
      try {
        await denyAccess();
      } catch (e) {
        console.error("Deny access error:", e);
      }
      dispatch(setAccess(false));
      navigate("/", { replace: true });
      return;
    }
    try {
      await dispatch(logout()).unwrap();
    } finally {
      navigate("/login", { replace: true });
    }
  };

  return (
    <div
      className="h-screen flex flex-col overflow-hidden"
      style={{
        background: R.bg,
        fontFamily: FONT,
        color: R.text,
      }}
    >
      {/* Top bar — unified spec */}
      <div
        className="flex items-center justify-between flex-shrink-0"
        style={{
          padding: isNarrow ? "10px 16px" : "11px 28px",
          minHeight: isNarrow ? 48 : 60,
          borderBottom: `1px solid ${R.border}`,
          background: access
            ? `radial-gradient(ellipse 40% 120% at 100% 50%, ${R.brandTint} 0%, transparent 70%), ${R.surface}`
            : R.surface,
          gap: 10,
        }}
      >
        <div
          className="flex items-center"
          style={{ gap: 10, minWidth: 0, flex: 1 }}
        >
          <BackButton onClick={() => navigate("/")} />
          <img
            src="/logo-ts.png"
            alt="UnrealSoft"
            style={{
              width: isNarrow ? 26 : 34,
              height: isNarrow ? 26 : 34,
              objectFit: "contain",
              flexShrink: 0,
              userSelect: "none",
            }}
          />
          <div
            style={{
              minWidth: 0,
              flex: 1,
              lineHeight: 1,
            }}
          >
            <h1
              style={{
                fontSize: isNarrow ? 16 : 18,
                fontWeight: 700,
                letterSpacing: -0.3,
                margin: 0,
                color: access ? "var(--color-brand-ink)" : R.ink,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.2,
              }}
            >
              {t("changelog.title")}
            </h1>
            {!isNarrow && latestVersion && (
              <div
                style={{
                  marginTop: 4,
                  fontSize: 11,
                  fontFamily: R.mono,
                  fontWeight: 500,
                  color: R.textMuted,
                  letterSpacing: 0.3,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                v{latestVersion}
              </div>
            )}
          </div>
        </div>

        <div
          className="flex items-center"
          style={{ gap: isNarrow ? 4 : 10, flexShrink: 0 }}
        >
          {username && (
            <ProfileDropdown
              displayName={displayName}
              username={username}
              access={access}
              isNarrow={isNarrow}
              onLogout={handleLogout}
            />
          )}
        </div>
      </div>

      {/* Main */}
      <main
        className="flex-1 min-h-0 overflow-y-auto"
        style={{ padding: isNarrow ? "16px 14px 24px" : "22px 28px 32px" }}
      >
        {/* Page heading card — bento style, no shadow */}
        <section
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
            marginBottom: isNarrow ? 16 : 20,
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 11,
                background: R.brandTint,
                color: R.brandDeep,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <HistoryOutlined style={{ fontSize: 20 }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 1.2,
                  textTransform: "uppercase",
                  color: R.textMuted,
                  marginBottom: 4,
                }}
              >
                {t("changelog.kicker")}
              </div>
              <h2
                style={{
                  margin: 0,
                  fontSize: isNarrow ? 22 : 28,
                  fontWeight: 700,
                  letterSpacing: -0.6,
                  color: R.ink,
                  lineHeight: 1.15,
                }}
              >
                {t("changelog.title")}
              </h2>
              <p
                style={{
                  margin: "6px 0 0",
                  fontSize: 13,
                  color: R.textSoft,
                  maxWidth: 560,
                  lineHeight: 1.5,
                }}
              >
                {t("changelog.subtitle")}
              </p>
            </div>
          </div>

          {latestVersion && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: 6,
              }}
            >
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: 1.2,
                  textTransform: "uppercase",
                  color: R.textMuted,
                }}
              >
                {t("changelog.currentVersion")}
              </span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 12px",
                  borderRadius: 999,
                  background: R.brandTint,
                  border: `1px solid rgba(217,119,6,0.25)`,
                  color: R.brandDeep,
                  fontSize: 13,
                  fontWeight: 700,
                  fontFamily: R.mono,
                  fontVariantNumeric: "tabular-nums",
                  letterSpacing: 0.3,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 999,
                    background: "var(--color-brand)",
                  }}
                />
                v{latestVersion}
              </span>
            </div>
          )}
        </section>

        {/* Legend */}
        <section
          style={{
            background: R.surface,
            border: `1px solid ${R.border}`,
            borderRadius: 14,
            padding: isNarrow ? "12px 14px" : "14px 18px",
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 10,
            marginBottom: isNarrow ? 16 : 22,
          }}
        >
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              color: R.textMuted,
              marginRight: 2,
            }}
          >
            {t("changelog.legend")}
          </span>
          {(
            Object.entries(typeConfig) as [
              ChangeType,
              (typeof typeConfig)[ChangeType],
            ][]
          ).map(([type, cfg]) => (
            <span
              key={type}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 10px 4px 6px",
                borderRadius: 999,
                background: cfg.tint,
                color: cfg.ink,
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: 0.1,
              }}
            >
              <span
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 999,
                  background: "rgba(255,255,255,0.6)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10.5,
                }}
              >
                {cfg.icon}
              </span>
              {t(cfg.labelKey)}
            </span>
          ))}
        </section>

        {/* Timeline / version cards */}
        <section
          style={{
            display: "flex",
            flexDirection: "column",
            gap: isNarrow ? 14 : 16,
          }}
        >
          {changelogData.map((release) => {
            const isLatest = release.version === latestVersion;
            const dateStr = new Date(release.date).toLocaleDateString(
              i18n.language,
              { year: "numeric", month: "short", day: "2-digit" },
            );
            return (
              <article
                key={release.version}
                style={{
                  background: R.surface,
                  border: `1px solid ${R.border}`,
                  borderRadius: 16,
                  overflow: "hidden",
                }}
              >
                {/* Card header */}
                <header
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    padding: isNarrow ? "14px 16px" : "16px 22px",
                    borderBottom: `1px solid ${R.border}`,
                    background: isLatest
                      ? `radial-gradient(ellipse 60% 200% at 0% 50%, ${R.brandTint} 0%, transparent 70%), ${R.surface}`
                      : R.surface,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      minWidth: 0,
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "5px 10px",
                        borderRadius: 8,
                        background: isLatest ? "#D97706" : R.bg,
                        border: isLatest
                          ? "none"
                          : `1px solid ${R.border}`,
                        color: isLatest ? "#fff" : R.ink,
                        fontSize: 13,
                        fontWeight: 700,
                        fontFamily: R.mono,
                        fontVariantNumeric: "tabular-nums",
                        letterSpacing: 0.3,
                      }}
                    >
                      v{release.version}
                    </span>
                    {isLatest && (
                      <span
                        style={{
                          fontSize: 10.5,
                          fontWeight: 700,
                          letterSpacing: 1.2,
                          textTransform: "uppercase",
                          color: R.brandDeep,
                        }}
                      >
                        {t("changelog.latest")}
                      </span>
                    )}
                  </div>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      color: R.textMuted,
                      fontFamily: R.mono,
                      letterSpacing: 0.3,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {dateStr}
                  </span>
                </header>

                {/* Entries */}
                <ul
                  style={{
                    margin: 0,
                    padding: 0,
                    listStyle: "none",
                  }}
                >
                  {release.entries.map((entry, idx) => {
                    const cfg = typeConfig[entry.type];
                    return (
                      <li
                        key={idx}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 12,
                          padding: isNarrow
                            ? "12px 16px"
                            : "14px 22px",
                          borderTop:
                            idx === 0 ? "none" : `1px solid ${R.border}`,
                        }}
                      >
                        <span
                          title={t(cfg.labelKey)}
                          style={{
                            flexShrink: 0,
                            width: 28,
                            height: 28,
                            borderRadius: 8,
                            background: cfg.tint,
                            color: cfg.ink,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 13,
                            marginTop: 1,
                          }}
                        >
                          {cfg.icon}
                        </span>
                        <span
                          style={{
                            flex: 1,
                            fontSize: 13.5,
                            lineHeight: 1.55,
                            color: R.text,
                            fontWeight: 500,
                          }}
                        >
                          {entry.text}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </article>
            );
          })}
        </section>
      </main>
    </div>
  );
}

